import React, { Dispatch, ReactNode, SetStateAction, createContext, useCallback, useContext, useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { exchangeImpersonationCode, getFarmFeatureFlags, getFarmFeatureFlagsAdmin, getUserData, logMixpanelId, verifyUser } from "services";

import {
  bailOutOfImpersonation,
  captureQueryParams,
  changeLanguage,
  clearPersistedQueryCache,
  clearStoredFarmLocations,
  getEnvironment,
  getFarmIdFromLocalStorage,
  getImpersonationToken,
  getLocalKlimVar,
  getPendingImpersonationCode,
  isCrowdinInContextSupported,
  isImpersonating,
  mapAppTypeToString,
  markInternalTraffic,
  productAnalytics,
  setImpersonationToken,
  setLocalKlimVar,
  setLocalStorageFarmFeatureFlags,
  useAppType,
  useCacheInvalidation,
  useCancelAsyncOnUnmount,
  useGetFarmFeatureFlags,
  useIdentifyFeatureFlags,
  useLocation,
  useNavigate,
  useNetworkState,
  useTranslation,
} from "utils";

import { FARM_INDEPENDENT_QUERY_KEYS, LANGUAGE_CODE, SOILSIGHT_ROUTE } from "consts";

import { User } from "models";

import { Message as message } from "components";

import { Verifying } from "pages/app/account/Verifying";

export type UserContextType = {
  user: User | null;
  setUser: Dispatch<SetStateAction<User | null>>;
  setTermsOverlayHidden: Dispatch<SetStateAction<boolean>>;
  termsOverlayHidden: boolean;
  reloadUserData: () => Promise<void>;
  verifying: boolean;
  switchFarm: (farmId: string | null) => void;
};

// create user context provider
const UserContext = createContext<UserContextType>({
  user: null,
  setUser: () => null,
  reloadUserData: () => Promise.resolve(),
  verifying: true,
  switchFarm: () => null,
  setTermsOverlayHidden: () => null,
  termsOverlayHidden: false,
});

// set paths that should be available for non-signed in users
const noRedirect = ["/t/", "/sign-in", "/sign-up", "/confirm-account", "/password", SOILSIGHT_ROUTE, "/sign-out", "/update-app", "/page"];

export const UserContextProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("global");
  const appType = useAppType();
  const network = useNetworkState();
  const signal = useCancelAsyncOnUnmount()();
  const queryClient = useQueryClient();

  const [currentLocation, setCurrentLocation] = useState({
    path: location.pathname,
    search: location.search,
  });
  const [termsOverlayHidden, setTermsOverlayHidden] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  // Seed true when a handover code is pending, so the splash covers the exchange (no sign-in flash).
  const [verifying, setVerifying] = useState(!!getLocalKlimVar("token") || isImpersonating());
  const [farmIdPendingQueryInvalidation, setFarmIdPendingQueryInvalidation] = useState<string | null>(null);

  // Both 60s polls stay off while impersonating (an idle tab shouldn't keep the session alive, and the
  // admin shouldn't churn the farmer's cache). Initial feature flags still load via switchFarm on handover.
  useCacheInvalidation(!!user && !isImpersonating(), user?.id);

  useIdentifyFeatureFlags(user ? { id: user.id, email: user.email, farmId: user.farm?.id, country: user.farm?.country } : null);

  const { data: farmFeatureFlags, isFetched: isFarmFeatureFlagsFetched } = useGetFarmFeatureFlags({ farmId: isImpersonating() ? "" : user?.farm?.id || "" });

  const shouldRedirectToProfileRoutes = !noRedirect.some((v) => location.pathname.includes(v));
  // /soilsight is the one noRedirect path where a signed-in visitor has a real destination
  // (/scenarios-landing-page), picked by its own gate ... the /home redirect below would pre-empt it.
  const skipPostLoginHomeRedirect = location.pathname.includes(SOILSIGHT_ROUTE);

  // redirect to sign in or content pages if user not logged in
  const goToSigninMaybe = useCallback(() => {
    if (location.pathname.includes("/news") || location.pathname.includes("/article") || location.pathname.includes("/community")) {
      navigate(location.pathname.replace("", ""));
    } else if (shouldRedirectToProfileRoutes) {
      setVerifying(false);
      // setLocalKlimVar("urlSlice", location.pathname.split(process.env.REACT_APP_FARM_URL)[1]); // this probably needs some other logic, disabling for now
      navigate("/sign-in");
    }
  }, [navigate, location, shouldRedirectToProfileRoutes]);

  const switchFarm = useCallback((farmId: string | null) => {
    setLocalKlimVar("farmId", farmId);

    // Fetch and store farm feature flags when switching to another farm
    if (farmId) {
      productAnalytics.group(farmId);
      const isAdmin = getEnvironment().admin;
      const queryFn = isAdmin ? getFarmFeatureFlagsAdmin : getFarmFeatureFlags;

      queryFn({ farmId })
        .then((response) => {
          setLocalStorageFarmFeatureFlags(response);

          // Defer the re-query until the user context reflects the new farm (see effect below).
          // Invalidating right away would refetch still-mounted queries keyed to the previous
          // farm while the farm-id request header already points at the new farm, so the
          // backend rejects them with 401/404 (e.g. when switching farms from the all-fields
          // breadcrumb, where farm-scoped queries stay mounted during the switch).
          setFarmIdPendingQueryInvalidation(farmId);
        })
        .catch((_err) => {
          setLocalStorageFarmFeatureFlags([]);
        });
    }
  }, []);

  // we may need to re-query after a farm switch, if the feature flags differ between farms.
  // Farm-independent query keys (crop footprints, reference tables, platform-wide queries,
  // practices) are excluded, since their data doesn't vary by farm. Runs only once the user
  // context carries the new farm, so queries keyed to the previous farm are inactive by now
  // and are only marked stale instead of being refetched with a mismatching farm-id header.
  useEffect(() => {
    if (!farmIdPendingQueryInvalidation || user?.farm?.id !== farmIdPendingQueryInvalidation) return;

    setFarmIdPendingQueryInvalidation(null);
    queryClient.invalidateQueries({
      predicate: (query) => !FARM_INDEPENDENT_QUERY_KEYS.includes(query.queryKey[0] as (typeof FARM_INDEPENDENT_QUERY_KEYS)[number]),
    });
  }, [farmIdPendingQueryInvalidation, user?.farm?.id, queryClient]);

  // if user cannot be verified, continue to redirection
  const clearUser = useCallback(() => {
    // While impersonating, a failed verify/load must bail out, not wipe the admin's own localStorage token.
    if (isImpersonating()) {
      bailOutOfImpersonation();
      return;
    }

    if (getLocalKlimVar("token")) {
      message({ content: t("errors.unverified"), icon: "error" });

      // Only when a session actually ends here. Without a token this runs on every navigation of a
      // signed-out visitor, and wiping the cache each time would be pointless churn.
      clearPersistedQueryCache();
      clearStoredFarmLocations();
      // We reset here for the same reason. A reset on each navigation gives a signed-out visitor a new id per page and splits the signup funnel.
      productAnalytics.reset();
    }

    setUser(null);
    setVerifying(false);
    setLocalKlimVar("token", null);

    // Reset farm id, because if a different user tries to sign in, the old farm id
    // would be used leading to the backend returning 401
    switchFarm(null);
    goToSigninMaybe();
  }, [t, switchFarm, goToSigninMaybe]);

  // set user details after data collection
  const setLocalStorage = useCallback(
    async (details: User) => {
      if (details?.language) {
        const languageFromLocalStorage = getLocalKlimVar("language");
        // Acholi is a local in-context translation language and never overwrites the profile.
        // Keep an active Acholi selection on reload only where Crowdin in-context can run
        // (secure https, non-production); otherwise fall through to the profile language so
        // local http reverts cleanly instead of re-engaging Crowdin.
        if (languageFromLocalStorage === LANGUAGE_CODE.ACHOLI && isCrowdinInContextSupported()) {
          await changeLanguage(LANGUAGE_CODE.ACHOLI, user);
        } else if (languageFromLocalStorage !== details.language) {
          await changeLanguage(details.language, user);
        }
      }

      const countryFromLocalStorage = getLocalKlimVar("country");
      if (countryFromLocalStorage !== details?.farm?.country) {
        setLocalKlimVar("country", details?.farm?.country);
      }

      if (details?.farm?.id) {
        const farmIdFromLocalStorage = getFarmIdFromLocalStorage();

        if (farmIdFromLocalStorage !== details.farm.id) {
          switchFarm(details.farm.id);
        }
      }
    },
    [user, switchFarm]
  );

  // update user's mixpanel id if needed
  const updateMixpanelId = useCallback(
    (mixpanelId: string) => {
      if (isImpersonating()) return;
      logMixpanelId({ id: mixpanelId, signal });
    },
    [signal]
  );

  // get user data
  const loadUser = useCallback(() => {
    getUserData({
      appType: mapAppTypeToString(appType),
      signal,
    })
      .then((res) => {
        setUser(res);
        setLocalStorage(res);
        markInternalTraffic(res?.email);
        productAnalytics.identify(res);

        if (getLocalKlimVar("sessionId")) {
          updateMixpanelId(getLocalKlimVar("sessionId"));
        }

        setTimeout(() => setVerifying(false), 400); // min verification time so we don't have a flash in/out splash page

        if (shouldRedirectToProfileRoutes && !location.pathname.includes("")) {
          navigate(`${location.pathname ? location.pathname : "/home"}${location.search}`);
        } else if (!shouldRedirectToProfileRoutes && !skipPostLoginHomeRedirect) {
          navigate("/home");
        }
      })
      .catch((_err) => {
        clearUser();
      });
  }, [
    appType,
    clearUser,
    signal,
    navigate,
    setLocalStorage,
    updateMixpanelId,
    shouldRedirectToProfileRoutes,
    skipPostLoginHomeRedirect,
    location.pathname,
    location.search,
  ]);

  // update user data from child pages
  const reloadUserData = useCallback(
    () =>
      getUserData({
        appType: mapAppTypeToString(appType),
        signal,
      }).then((res) => {
        setUser(res);
        setLocalStorage(res);
        productAnalytics.identify(res);
      }),
    [appType, signal, setLocalStorage]
  );

  // verify user
  const verifyUserSession = () => {
    if (isImpersonating()) {
      if (getImpersonationToken()) {
        loadUser();
      } else if (!getPendingImpersonationCode()) {
        bailOutOfImpersonation();
      }
      return;
    }

    const token = getLocalKlimVar("token");
    if (network && token && token !== "null" && !location.pathname.includes("/t/") && !location.pathname.includes("/sign-out/")) {
      verifyUser({
        appType: mapAppTypeToString(appType),
        signal,
      })
        .then((res) => {
          if (res.verified) {
            setLocalKlimVar("token", res.token);
            loadUser();
          } else {
            clearUser();
          }
        })
        .catch((_err) => {
          clearUser();
        });
    } else if (network && location.pathname.includes("/") && !location.pathname.includes("/t/")) {
      clearUser();
    } else if (network && !location.pathname.includes("/t/")) {
      setUser(null);
      setVerifying(false);
    } else if (location.pathname.includes("/t/")) {
      setVerifying(false);
    }
  };

  // exchange the one-time code for a token before the verify effect runs (which
  // stays paused until then), then load the farmer
  useEffect(() => {
    const code = getPendingImpersonationCode();
    if (!isImpersonating() || getImpersonationToken() || !code) return;

    exchangeImpersonationCode({ code })
      .then(async (res) => {
        setImpersonationToken(res.token);
        // Drop the admin's own persisted cache (rehydrated on boot) so it can't bleed in, queries refetch fresh.
        clearStoredFarmLocations();
        await clearPersistedQueryCache();
        if (res.farmId) switchFarm(res.farmId);
        loadUser();
      })
      .catch(() => {
        bailOutOfImpersonation();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Remove param to avoid side effects if user switches farm later
    const params = new URLSearchParams(currentLocation.search);
    const farmId = params.get("farmId");

    if (farmId) {
      params.delete("farmId");
      navigate({
        search: params.toString(),
      });

      switchFarm(farmId);
    }

    // verify user
    verifyUserSession();
  }, [currentLocation.path, switchFarm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.pathname !== currentLocation.path || location.search !== currentLocation.search) {
      setCurrentLocation({
        path: location.pathname,
        search: location.search,
      });
    }
  }, [currentLocation, location]);

  useEffect(() => {
    captureQueryParams(location.search);
  }, [location.search]);

  useEffect(() => {
    if (currentLocation.path === "/sign-out") {
      setTermsOverlayHidden(false);
    }
  }, [currentLocation.path]);

  if (isFarmFeatureFlagsFetched) {
    setLocalStorageFarmFeatureFlags(farmFeatureFlags ?? []);
  }

  return (
    <UserContext.Provider
      value={{
        user,
        reloadUserData,
        setTermsOverlayHidden,
        setUser,
        termsOverlayHidden,
        verifying,
        switchFarm,
      }}
    >
      {verifying ? <Verifying /> : children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);

export const UserContextConsumer = UserContext.Consumer;
